import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface Patient {
  id: string
  name: string
  species: string
  breed: string
  date_of_birth: string | null
  weight: number | null
  weight_unit: string
  sex: string
  spay_neuter_status: boolean
  microchip_number: string
  allergies: string
  owner_name: string
  owner_phone: string
  owner_email: string
  notes: string
  status: string
  created_at: string
  updated_at: string
}

export interface PatientTreatment {
  id: string
  treatment_number: number
  patient_id: string
  veterinarian_id: string | null
  treatment_type: string
  diagnosis: string
  status: string
  treatment_date: string
  patient_name: string
  patient_species: string
  veterinarian_name: string | null
}

export interface PatientPrescription {
  id: string
  prescription_number: number
  patient_id: string
  medication_id: string
  dosage: string
  frequency: string
  start_date: string
  end_date: string | null
  status: string
  medication_name: string
  patient_name: string
  veterinarian_name: string | null
}

export interface PatientLabOrder {
  id: string
  lab_order_number: number
  patient_id: string
  test_name: string
  status: string
  date_ordered: string
  date_completed: string | null
  results: string
  veterinarian_name: string | null
}

interface PatientDetailState {
  patient: Patient | null
  treatments: PatientTreatment[]
  prescriptions: PatientPrescription[]
  labOrders: PatientLabOrder[]
  loading: boolean
  error: string | null
}

const initialState: PatientDetailState = {
  patient: null,
  treatments: [],
  prescriptions: [],
  labOrders: [],
  loading: false,
  error: null,
}

export const fetchPatient = createAsyncThunk(
  'patientDetail/fetchPatient',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/patients/${id}`)
    if (!res.ok) throw new Error('Failed to fetch patient')
    return res.json() as Promise<Patient>
  }
)

export const updatePatient = createAsyncThunk(
  'patientDetail/updatePatient',
  async ({ id, data }: { id: string; data: Partial<Patient> }) => {
    const res = await fetch(`/.netlify/functions/patients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update patient')
    return res.json() as Promise<Patient>
  }
)

export const fetchPatientTreatments = createAsyncThunk(
  'patientDetail/fetchTreatments',
  async (patientId: string) => {
    const res = await fetch(`/.netlify/functions/patient-treatments?patient_id=${patientId}`)
    if (!res.ok) throw new Error('Failed to fetch treatments')
    return res.json() as Promise<PatientTreatment[]>
  }
)

export const fetchPatientPrescriptions = createAsyncThunk(
  'patientDetail/fetchPrescriptions',
  async (patientId: string) => {
    const res = await fetch(`/.netlify/functions/patient-prescriptions?patient_id=${patientId}`)
    if (!res.ok) throw new Error('Failed to fetch prescriptions')
    return res.json() as Promise<PatientPrescription[]>
  }
)

export const fetchPatientLabOrders = createAsyncThunk(
  'patientDetail/fetchLabOrders',
  async (patientId: string) => {
    const res = await fetch(`/.netlify/functions/patient-lab-orders?patient_id=${patientId}`)
    if (!res.ok) throw new Error('Failed to fetch lab orders')
    return res.json() as Promise<PatientLabOrder[]>
  }
)

const patientDetailSlice = createSlice({
  name: 'patientDetail',
  initialState,
  reducers: {
    clearPatient(state) {
      state.patient = null
      state.treatments = []
      state.prescriptions = []
      state.labOrders = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatient.pending, (state) => {
        if (!state.patient) state.loading = true
      })
      .addCase(fetchPatient.fulfilled, (state, action) => {
        state.patient = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchPatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.patient = action.payload
      })
      .addCase(fetchPatientTreatments.fulfilled, (state, action) => {
        state.treatments = action.payload
      })
      .addCase(fetchPatientPrescriptions.fulfilled, (state, action) => {
        state.prescriptions = action.payload
      })
      .addCase(fetchPatientLabOrders.fulfilled, (state, action) => {
        state.labOrders = action.payload
      })
  },
})

export const { clearPatient } = patientDetailSlice.actions
export default patientDetailSlice.reducer
